import { toUpperHex } from "@/util/color";
import {
  BRAND_SWATCH_SECTION,
  getColorPickerSelectValue,
  getColorPickerUpdateValue,
  type ColorPickerSelectPayload,
} from "@/util/colorPicker";
import { ref } from "vue";

interface UseColorPickerValueHandlersOptions {
  getCommittedColor: () => unknown;
  onColor: (value: string) => void;
  onSelect?: (value: string) => void;
}

/**
 * @description Shared HLColorPicker value handlers for select/live updates
 * with rollback support on back/close events.
 */
export const useColorPickerValueHandlers = ({
  getCommittedColor,
  onColor,
  onSelect,
}: UseColorPickerValueHandlersOptions) => {
  const snapshotColor = ref<string | null>(null);

  const clearSnapshot = () => {
    snapshotColor.value = null;
  };

  const restoreSnapshot = () => {
    if (snapshotColor.value == null) {
      return;
    }
    onColor(snapshotColor.value);
    clearSnapshot();
  };

  const handleSelect = (
    payload: ColorPickerSelectPayload,
    sectionKey?: string
  ) => {
    const colorValue = getColorPickerSelectValue(payload);
    if (colorValue == null) {
      return;
    }
    if (sectionKey === BRAND_SWATCH_SECTION) {
      onSelect?.(colorValue);
    }
    onColor(colorValue);
    clearSnapshot();
  };

  const handlePickerUpdate = (...args: Array<unknown>) => {
    const colorValue = getColorPickerUpdateValue(...args);
    if (colorValue == null) {
      return;
    }
    if (snapshotColor.value == null) {
      snapshotColor.value = toUpperHex(getCommittedColor());
    }
    onColor(colorValue);
  };

  const handlePickerBack = (save?: boolean, deleteColor?: boolean) => {
    if (save || deleteColor) {
      clearSnapshot();
      return;
    }
    restoreSnapshot();
  };

  const handlePickerClose = () => {
    restoreSnapshot();
  };

  return {
    handleSelect,
    handlePickerUpdate,
    handlePickerBack,
    handlePickerClose,
  } as const;
};
